/**
 * A location's promo code, edited from the Edit Location form.
 *
 * There is no promo column on `locations`. A location's own code is the `promo` parameter of its
 * active location-scoped organization CTA — the same value the Locations tab and the event Location
 * dropdown ("· promo X") read. A location without one falls back to the organization's code.
 *
 * Every write goes through the same organization-CTA service the Calls to Action tab uses
 * (createOrganizationCTA / updateOrganizationCTA / generateOrganizationCTAQRCode): the link is
 * always rebuilt by buildOrganizationCTAUrl, never written by hand.
 *
 * What a change does NOT reach (shown to the user before saving):
 *   - Printed QR codes. A CTA's QR encodes its full generated URL, promo included, so codes that
 *     were already printed keep sending the old promo until they are reprinted.
 *   - Upcoming events that already have their own event CTA. get_event_promo_codes resolves an
 *     event's code from its OWN CTA first (copied from the source CTA when it was set up), so those
 *     events keep their current code; events without their own CTA follow the location.
 */

import { supabase } from "./supabase";
import {
  createOrganizationCTA,
  updateOrganizationCTA,
  generateOrganizationCTAQRCode,
} from "./organizationCTA.service";
import type { OrganizationCTA } from "../types/organizationCTA.types";
import type { Organization } from "../types/organization.types";
import type { UUID } from "../types/common.types";

/** Select value meaning "no code of its own — use the organization's". */
export const USE_ORG_DEFAULT = "__org_default__";

const CTA_COLUMNS = `
  *,
  offer:offers(id, name, offer_code, offer_type, value_type, value_amount, description),
  location:locations(id, name, state, city, affiliate_id)
`;

const promoOf = (
  cta: { parameters?: Record<string, unknown> | null } | null,
) =>
  typeof cta?.parameters?.promo === "string" ? cta.parameters.promo.trim() : "";

export interface LocationPromoContext {
  /** The active location CTA that carries the location's code (or the newest active one). */
  locationCta: OrganizationCTA | null;
  /** The active organization-level CTA a new location CTA is copied from. */
  orgCta: OrganizationCTA | null;
  /** The location's own code, or "" when it uses the organization's. */
  currentCode: string;
}

export async function getLocationPromoContext(
  organizationId: UUID,
  locationId: UUID | undefined,
): Promise<LocationPromoContext> {
  const { data: orgCtas, error: orgErr } = await supabase
    .from("organization_ctas")
    .select(CTA_COLUMNS)
    .eq("organization_id", organizationId)
    .eq("is_active", true)
    .is("location_id", null)
    .order("updated_at", { ascending: false })
    .limit(50);
  if (orgErr) {
    throw orgErr;
  }
  const orgList = (orgCtas ?? []) as OrganizationCTA[];
  const orgCta =
    orgList.find((c) => c.cta_type === "subscription") ?? orgList[0] ?? null;

  if (!locationId) {
    return { locationCta: null, orgCta, currentCode: "" };
  }

  const { data: locCtas, error: locErr } = await supabase
    .from("organization_ctas")
    .select(CTA_COLUMNS)
    .eq("location_id", locationId)
    .eq("is_active", true)
    .order("updated_at", { ascending: false })
    .limit(50);
  if (locErr) {
    throw locErr;
  }
  const locList = (locCtas ?? []) as OrganizationCTA[];
  // Same rule the Locations tab uses: the location's code is the promo on its active location CTA.
  const locationCta =
    locList.find((c) => promoOf(c) !== "") ?? locList[0] ?? null;
  return { locationCta, orgCta, currentCode: promoOf(locationCta) };
}

/**
 * Upcoming, not-cancelled events linked to this location whose OWN event CTA carries a code other
 * than `effectiveCode`. Those events keep their current code after the change.
 */
export async function countUpcomingEventsKeepingTheirCode(
  locationId: UUID,
  effectiveCode: string,
): Promise<number> {
  const { data: links, error: linkErr } = await supabase
    .from("event_locations")
    .select("event_id")
    .eq("location_id", locationId)
    .limit(1000);
  if (linkErr) {
    throw linkErr;
  }
  const linked = [
    ...new Set((links ?? []).map((l: { event_id: string }) => l.event_id)),
  ];
  if (linked.length === 0) {
    return 0;
  }
  const { data: events, error: evErr } = await supabase
    .from("events")
    .select("id")
    .in("id", linked)
    .gt("start_time", new Date().toISOString())
    .neq("status", "cancelled")
    .limit(1000);
  if (evErr) {
    throw evErr;
  }
  const upcoming = (events ?? []).map((e: { id: string }) => e.id);
  if (upcoming.length === 0) {
    return 0;
  }
  const { data: ctas, error: ctaErr } = await supabase
    .from("event_call_to_actions")
    .select("event_id, parameters")
    .in("event_id", upcoming)
    .limit(2000);
  if (ctaErr) {
    throw ctaErr;
  }
  const keeping = new Set<string>();
  for (const c of (ctas ?? []) as Array<{
    event_id: string;
    parameters: Record<string, unknown> | null;
  }>) {
    const own = promoOf(c);
    if (own && own !== effectiveCode) {
      keeping.add(c.event_id);
    }
  }
  return keeping.size;
}

export type LocationPromoResult =
  | { kind: "unchanged" }
  | { kind: "updated"; regenerated: boolean; regenerateFailed: boolean }
  | { kind: "created" }
  | { kind: "cleared"; regenerated: boolean; regenerateFailed: boolean };

/**
 * Apply the chosen code. `choice` is a promo code, or USE_ORG_DEFAULT.
 *
 * - Location CTA exists, code chosen  -> set its promo parameter.
 * - Location CTA exists, org default  -> remove its promo parameter (the Calls to Action tab's own
 *   "remove parameter"), keeping the CTA and its history. The link then carries no promo of its
 *   own and the location displays the organization's code.
 * - No location CTA, code chosen      -> create one, copied from the organization-level CTA (same
 *   fields the CTA tab sets), scoped to this location with the location's affiliate ID.
 * - No location CTA, org default      -> nothing to do.
 *
 * When the CTA already has a generated link/QR, it is regenerated so "Copy link" and the QR image
 * carry the new code (the CTA tab does the same for URL-only settings). Printed codes do not change.
 */
export async function applyLocationPromo(args: {
  organization: Organization;
  locationId: UUID;
  location: { affiliate_id?: string | null; facility_id?: string | null };
  choice: string;
  context: LocationPromoContext;
  userId: UUID;
}): Promise<LocationPromoResult> {
  const { organization, locationId, location, choice, context, userId } = args;
  const wantDefault = choice === USE_ORG_DEFAULT;
  const target = wantDefault ? "" : choice.trim();

  if (target === context.currentCode) {
    return { kind: "unchanged" };
  }

  const regenerateIfLinked = async (cta: OrganizationCTA) => {
    if (!cta.generated_url) {
      return { regenerated: false, regenerateFailed: false };
    }
    try {
      await generateOrganizationCTAQRCode(cta.id, organization, userId);
      return { regenerated: true, regenerateFailed: false };
    } catch {
      return { regenerated: false, regenerateFailed: true };
    }
  };

  const existing = context.locationCta;
  if (existing) {
    const parameters: Record<string, string> = {
      ...(existing.parameters ?? {}),
    };
    if (wantDefault) {
      delete parameters.promo;
    } else {
      parameters.promo = target;
    }
    const updated = await updateOrganizationCTA(
      existing.id,
      { parameters },
      userId,
    );
    const regen = await regenerateIfLinked({ ...existing, ...updated });
    return wantDefault
      ? { kind: "cleared", ...regen }
      : { kind: "updated", ...regen };
  }

  if (wantDefault) {
    return { kind: "unchanged" };
  }

  const src = context.orgCta;
  // A voucher CTA scoped to a location needs that location's Facility ID (CTA tab rule).
  if (src?.reward_type === "voucher" && !(location.facility_id ?? "").trim()) {
    throw new Error(
      "This organization's CTA issues vouchers, and this location has no Facility ID. " +
        "Add a Facility ID, or set the code from the Calls to Action tab.",
    );
  }
  const baseParams: Record<string, string> = { ...(src?.parameters ?? {}) };
  const affid = (location.affiliate_id ?? "").trim();
  if (affid) {
    baseParams.affid = affid;
  }
  baseParams.promo = target;

  await createOrganizationCTA(
    {
      organization_id: organization.id,
      location_id: locationId,
      name: src?.name ? `${src.name} (location)` : "Location signup",
      cta_type: src?.cta_type ?? "subscription",
      base_url: src?.base_url ?? undefined,
      parameters: baseParams,
      reward_type: src?.reward_type,
      offer_id: src?.offer_id ?? undefined,
      voucher_amount: src?.voucher_amount ?? undefined,
      voucher_expiry_days: src?.voucher_expiry_days ?? undefined,
      voucher_start_date: src?.voucher_start_date ?? undefined,
      voucher_end_date: src?.voucher_end_date ?? undefined,
      include_state_param: src?.include_state_param,
      use_state_defaults: src?.use_state_defaults,
      single_voucher: src?.single_voucher,
      use_member_signup: src?.use_member_signup,
      display_away_plans: src?.display_away_plans,
      allow_variable_voucher_value: src?.allow_variable_voucher_value,
      sales_position_id: src?.sales_position_id ?? undefined,
    },
    userId,
  );
  return { kind: "created" };
}
